import { createSelector } from '@reduxjs/toolkit';
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { ProjectId } from 'api/models';
import { Action, EventType, ObjectType } from 'api/pushes';
import { RootState } from 'data';
import { useProjectWSUpdates } from 'hooks/use-project-ws-updates';
import { getProjectMembersQueryKey } from 'queries';
import {
  getPlaceholderResolutionByProjectQueryKeyV2,
  PlaceholderResolutionsQueryKey,
} from 'queries/placeholder';
import { Content, Empty } from 'stores/core';
import { Memberships } from 'stores/memberships';
import { useLoadMembership } from './use-load-memberships';
import { useProjectMembersWSHandler } from './use-project-members-ws-handler';

const membershipSelector = (projectId: number) =>
  createSelector(
    (state: RootState) => state.memberships,
    (memberships) => memberships.byProjectId[projectId] ?? Empty(),
  );

export const useMemberships = (
  [, projectId]: ProjectId,
  forceReload = false,
): Content<Memberships> => {
  const memberships_store = useSelector(membershipSelector(projectId));
  const [canReload, setCanReload] = useState(forceReload);
  const [status] = memberships_store;

  const loadMembership = useLoadMembership();

  const load = useCallback(async () => {
    await loadMembership(ProjectId(projectId));
  }, [loadMembership, projectId]);

  // if forced, call API even if membership status is "loaded"
  useEffect(() => {
    if (!canReload) {
      return;
    }
    setCanReload(false);
    if (status !== 'loaded') {
      return;
    }
    load();
  }, [load, status, canReload, setCanReload]);

  useEffect(() => {
    if (status !== 'empty') {
      return;
    }

    load();
  }, [status, load]);

  const queryClient = useQueryClient();
  const invalidateQueries = useCallback(async () => {
    await queryClient.invalidateQueries(
      getProjectMembersQueryKey(ProjectId(projectId)),
    );
    await queryClient.invalidateQueries(
      getPlaceholderResolutionByProjectQueryKeyV2(ProjectId(projectId)),
    );
    await queryClient.invalidateQueries(PlaceholderResolutionsQueryKey);
  }, [projectId, queryClient]);
  const { handleMembersAdd, handleMembersDelete } = useProjectMembersWSHandler({
    onSuccess: () => invalidateQueries(),
  });

  useProjectWSUpdates(
    projectId,
    handleMembersAdd,
    EventType(Action.Create, ObjectType.ProjectMembers),
  );
  useProjectWSUpdates(
    projectId,
    handleMembersAdd,
    EventType(Action.Update, ObjectType.ProjectMembers),
  );
  useProjectWSUpdates(
    projectId,
    handleMembersDelete,
    EventType(Action.Delete, ObjectType.ProjectMembers),
  );

  return memberships_store;
};
